import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { canShowStaffCosts } from "../../../config/platformFeatures";
import { useCatalog, useSprints } from "../hooks/useActivityData";
import ActividadDetailModal from "../components/ActividadDetailModal";
import EpicFormModal from "../components/EpicFormModal";
import AssignTicketsModal from "../components/AssignTicketsModal";
import { activityErrorMessage, fetchRoadmap, type ActivityRow, type RoadmapRow } from "../services/activitiesService";
import { STATUS_TONE, dayDiff, formatMinutes, formatMoney, githubUrl, isGithubActivity, shortDate, todayLocal, weeksBetween } from "../lib/format";
import { applyScope } from "../lib/activityScope";
import ActivityScopeFilters, { useActivityScope } from "../components/ActivityScopeFilters";
import { useShowGithub } from "../hooks/useShowGithub";

// ---------------------------------------------------------------------------
// Roadmap (#1304): una fila por épica (milestone de GitHub o ticket con hijos)
// con su avance y sus fechas sobre una línea de tiempo por semanas. CSS grid,
// sin librerías: una columna por semana.
// ---------------------------------------------------------------------------

export default function RoadmapSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const { catalog } = useCatalog();
  const { sprints } = useSprints();
  const [allRows, setAllRows] = useState<RoadmapRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [assignFor, setAssignFor] = useState<ActivityRow | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const { scope, setScope, projects } = useActivityScope();
  const [showGithub, setShowGithub] = useShowGithub();
  // Épicas visibles: las del alcance elegido y, si el interruptor está apagado, sin los milestones de GitHub.
  const rows = useMemo(() => {
    const epics = applyScope(allRows.map((r) => r.epic), scope, projects);
    const keep = new Set(epics.filter((e) => showGithub || !isGithubActivity(e)).map((e) => e.id));
    return allRows.filter((r) => keep.has(r.epic.id));
  }, [allRows, scope, projects, showGithub]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ActivityRow | null>(null);

  useEffect(() => {
    let alive = true;
    fetchRoadmap()
      .then((data) => alive && setAllRows(data))
      .catch((err) => alive && setError(activityErrorMessage(err) ?? t("staffActivities.common.error")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [t, reloadKey]);

  const today = todayLocal();
  // Ventana de la línea de tiempo: de la fecha más temprana a la más tardía
  // (acotada a ~26 semanas alrededor de hoy para que quepa en pantalla).
  const { weeks, from } = useMemo(() => {
    const dates = rows.flatMap((r) => [r.startDate, r.dueDate]).filter((d): d is string => Boolean(d)).sort();
    let start = dates[0] ?? today;
    let end = dates[dates.length - 1] ?? today;
    if (start > today) start = today;
    if (end < today) end = today;
    const maxDays = 26 * 7;
    if (dayDiff(start, end) > maxDays) {
      const half = Math.floor(maxDays / 2);
      const t0 = new Date(`${today}T00:00:00`);
      const a = new Date(t0);
      a.setDate(a.getDate() - half);
      const b = new Date(t0);
      b.setDate(b.getDate() + half);
      start = a.toISOString().slice(0, 10);
      end = b.toISOString().slice(0, 10);
    }
    const w = weeksBetween(start, end);
    return { weeks: w, from: w[0] ?? start };
  }, [rows, today]);

  const cols = Math.max(weeks.length, 1);
  const totalDays = cols * 7;
  const pct = (date: string) => Math.min(100, Math.max(0, (dayDiff(from, date) / totalDays) * 100));
  // Sin costos donde Nómina/Staff están apagadas (WAYAK): regla de nómina.
  const showCost = canShowStaffCosts();
  const isOwner = showCost && rows.some((r) => r.cost);

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500/15 text-violet-500">
              <i className="ri-road-map-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>{t("staffActivities.roadmap.title")}</h1>
              <p className={`text-sm ${tc.textSecondary}`}>{t("staffActivities.roadmap.subtitle")}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ActivityScopeFilters scope={scope} setScope={setScope} projects={projects} rows={allRows.map((r) => r.epic)} tc={tc} />
            <button type="button" onClick={() => setCreating(true)} data-testid="roadmap-new-epic" className="flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-500">
              <i className="ri-flag-line" /> {t("staffActivities.epic.newEpic")}
            </button>
            <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.text}`} title={t("staffActivities.backlog.showGithubHint")}>
              <input type="checkbox" role="switch" aria-checked={showGithub} checked={showGithub} onChange={(e) => setShowGithub(e.target.checked)} className="h-4 w-4 accent-violet-500" data-testid="roadmap-show-github" />
              <i className="ri-github-line" /> {t("staffActivities.roadmap.showGithub")}
            </label>
          </div>
        </div>

        {notice && (
          <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-600" data-testid="roadmap-notice">
            {notice}
          </div>
        )}
        {loading && <p className={`text-sm ${tc.textSecondary}`}>{t("staffActivities.common.loading")}</p>}
        {error && <p className="text-sm text-red-500">{error}</p>}
        {!loading && !error && rows.length === 0 && allRows.length > 0 && <p className={`rounded-2xl border p-10 text-center text-sm ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`} data-testid="roadmap-filtered-empty">{t("staffActivities.scope.nothingInScope")}</p>}
        {!loading && !error && allRows.length === 0 && <p className={`rounded-2xl border p-10 text-center text-sm ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}>{t("staffActivities.roadmap.empty")}</p>}

        {rows.length > 0 && (
          <div className={`overflow-x-auto rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`} data-testid="roadmap">
            <div className="min-w-[900px]">
              {/* Cabecera de semanas */}
              <div className="grid" style={{ gridTemplateColumns: `minmax(280px, 1.2fr) repeat(${cols}, minmax(0, 1fr))` }}>
                <div className={`border-b px-4 py-2 text-xs font-semibold ${tc.cardBorder} ${tc.textSecondary}`}>{t("staffActivities.card.epic")}</div>
                {weeks.map((w) => (
                  <div key={w} className={`border-b border-l px-1 py-2 text-center text-[10px] ${tc.cardBorder} ${w <= today && dayDiff(w, today) < 7 ? "font-semibold text-violet-500" : tc.textSecondary}`}>
                    {t("staffActivities.roadmap.week")} {shortDate(w)}
                  </div>
                ))}
              </div>
              {rows.map((r) => {
                const a = r.epic;
                const gh = githubUrl(a);
                const hasDates = r.startDate && r.dueDate;
                const left = hasDates ? pct(r.startDate as string) : 0;
                const right = hasDates ? pct(r.dueDate as string) : 0;
                const width = hasDates ? Math.max(right - left, 1.5) : 0;
                const progress = Math.round((r.progress ?? 0) * 100);
                const late = Boolean(r.dueDate && r.dueDate < today && r.progress < 1);
                return (
                  <div key={a.id} data-testid={`roadmap-row-${a.id}`} className="grid" style={{ gridTemplateColumns: `minmax(280px, 1.2fr) repeat(${cols}, minmax(0, 1fr))` }}>
                    <div className={`border-b px-4 py-3 ${tc.cardBorder}`}>
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${STATUS_TONE[a.status]}`}>{t(`staffActivities.status.${a.status}`)}</span>
                        <button type="button" onClick={() => setDetail(a)} className={`truncate text-left text-sm font-medium ${tc.text} hover:underline`}>
                          {a.title}
                        </button>
                        <button type="button" onClick={() => setAssignFor(a)} title={t("staffActivities.epic.assign")} aria-label={t("staffActivities.epic.assign")} data-testid={`roadmap-assign-${a.id}`} className={`ml-auto rounded-md border px-1.5 py-0.5 text-[11px] ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}>
                          <i className="ri-git-merge-line" /> {t("staffActivities.epic.assign")}
                        </button>
                      </div>
                      <div className={`mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] ${tc.textSecondary}`}>
                        {gh ? (
                          <a href={gh} target="_blank" rel="noopener noreferrer" className="font-mono text-violet-500 hover:underline">
                            {a.key}
                          </a>
                        ) : (
                          <span className="font-mono">{a.key}</span>
                        )}
                        <span>{t("staffActivities.roadmap.children", { done: r.children.done, total: r.children.total })}</span>
                        {r.points.total > 0 && <span>{t("staffActivities.roadmap.points", { done: r.points.done, total: r.points.total })}</span>}
                        {r.loggedMinutes > 0 && (
                          <span>
                            {t("staffActivities.roadmap.logged")}: {formatMinutes(r.loggedMinutes)}
                          </span>
                        )}
                        {showCost && r.cost && (
                          <span className="text-emerald-600" data-testid={`roadmap-cost-${a.id}`}>
                            {formatMoney(r.cost.actual, r.cost.currency)}
                            {r.cost.estimated != null && ` / ${formatMoney(r.cost.estimated, r.cost.currency)}`}
                          </span>
                        )}
                      </div>
                      <div className={`mt-2 h-1.5 w-full overflow-hidden rounded-full ${isDarkMode ? "bg-dark-border" : "bg-gray-200"}`}>
                        <div className={`h-full rounded-full ${late ? "bg-red-500" : progress >= 100 ? "bg-emerald-500" : "bg-violet-500"}`} style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                    <div className={`relative border-b border-l ${tc.cardBorder}`} style={{ gridColumn: `span ${cols} / span ${cols}` }}>
                      {/* Hoy */}
                      <div className="absolute inset-y-0 w-px bg-violet-500/60" style={{ left: `${pct(today)}%` }} />
                      {hasDates ? (
                        <div
                          className={`absolute top-1/2 h-6 -translate-y-1/2 rounded-md ${late ? "bg-red-500/70" : "bg-violet-500/60"}`}
                          style={{ left: `${left}%`, width: `${width}%` }}
                          title={`${shortDate(r.startDate)} → ${shortDate(r.dueDate)} · ${progress}%`}
                        >
                          <div className="h-full rounded-md bg-emerald-500/80" style={{ width: `${progress}%` }} />
                        </div>
                      ) : (
                        <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-[11px] italic ${tc.textSecondary}`}>{t("staffActivities.roadmap.noDates")}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {creating && (
        <EpicFormModal
          catalog={catalog}
          onClose={() => setCreating(false)}
          onSaved={(epic) => {
            setCreating(false);
            setNotice(t("staffActivities.epic.created", { title: epic.title }));
            setReloadKey((k) => k + 1);
          }}
        />
      )}
      {assignFor && (
        <AssignTicketsModal
          epic={assignFor}
          onClose={() => setAssignFor(null)}
          onAssigned={(count) => {
            setAssignFor(null);
            setNotice(t("staffActivities.epic.assigned", { count }));
            setReloadKey((k) => k + 1);
          }}
        />
      )}
      {detail && (
        <ActividadDetailModal
          activity={detail}
          catalog={catalog}
          sprints={sprints}
          epics={rows.map((r) => r.epic)}
          isOwner={isOwner}
          canManage={false}
          onClose={() => setDetail(null)}
          onChanged={(next) => next && setAllRows((prev) => prev.map((r) => (r.epic.id === next.id ? { ...r, epic: { ...r.epic, ...next } } : r)))}
        />
      )}
    </div>
  );
}
